import { datadogRum } from '@datadog/browser-rum';
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';
import { useDeepCompareEffect } from 'react-use';

import FlexColumn from '@ui/atoms/FlexColumn';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import fullWidth from '@ui/styled/FullWidth';

import useInvoice from '@hooks/useMemberInvoice';

import { keysToCamel } from '@utils/butter';

import { IPageProps } from '@folx-types/flow-steps';

import BillDetails, { IBillDetailsContent } from './BillDetails.component';

interface IBillDetailsPageProps extends IPageProps {
	path: string;
	invoiceId?: string;
}

const BillDetailsPage: FC<IBillDetailsPageProps> = ({ invoiceId }) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "mssp-medication-bills-detail-step" }) {
				title
				breadcrumb_label
				bill_not_found_title
				bill_not_found_text
				bill_not_found_button_text
			}
		}
	`);

	const refinedContent: IBillDetailsContent = keysToCamel(pageContent) as IBillDetailsContent;

	const {
		data: invoice,
		loading: invoiceLoading,
		error: invoiceError,
	} = useInvoice(invoiceId ?? '');

	useDeepCompareEffect(() => {
		if (!invoiceError) return;
		datadogRum.addError(`Error loading invoice ${invoiceId}: ${JSON.stringify(invoiceError)}`);
		console.log(invoiceError);
	}, [invoiceError]);

	const loading = invoiceLoading;

	const FullWidthColumn = fullWidth(FlexColumn);

	const skeleton = (
		<FullWidthColumn gap="md">
			<Skeleton height="sm" fullWidth />
			<Skeleton height="lg" fullWidth />
			<Skeleton height="xxxxxl" fullWidth />
		</FullWidthColumn>
	);

	return (
		<LoadingWrapper loading={loading} skeleton={skeleton}>
			<BillDetails pageContent={refinedContent} invoice={invoice} invoiceId={invoiceId ?? ''} />
		</LoadingWrapper>
	);
};

export default BillDetailsPage;
